import { Controller } from "react-hook-form";
import CustomModal from "../../../ui/CustomModal/CustomModal";
import CustomBadge from "../../../ui/CustomBadge/CustomBadge";
import CustomSelect from "../../../ui/CustomSelect/CustomSelect";
import CustomNumberInput from "../../../ui/CustomNumberInput/CustomNumberInput";
import CustomButton from "../../../ui/CustomButton/CustomButton";
import {
  TRANSACTION_STATUS_MAP,
  SPLIT_CHANNEL_OPTIONS,
} from "../../constants/transactionConstants";
import {
  formatDateTimeToLocale,
  getDisplayValue,
  formatKES,
} from "../../../../utils/transactionutils";
import { splitFormValidators } from "../../utils/transactionValidators";

const SplitTransactionModal = ({
  opened,
  onClose,
  transactionData: {
    status,
    gatewayReference,
    transactionDate,
    amount,
  } = {},
  control,
  onSubmit = () => {},
  isSubmitting = false,
}) => {
  const backendStatus = status;
  const mappedStatus = TRANSACTION_STATUS_MAP[backendStatus] || "default";

  return (
    <CustomModal
      opened={opened}
      onClose={onClose}
      centered
      overlayProps={{ backgroundOpacity: 0.4, blur: 4 }}
      radius="md"
      size="lg"
      withCloseButton={!isSubmitting}
      closeOnClickOutside={!isSubmitting}
      closeOnEscape={!isSubmitting}
      styles={{ body: { padding: 0 } }}
    >
      <div
        style={{
          maxHeight: "calc(90dvh - 160px)",
          overflowY: "auto",
          padding: "0 1rem 1rem",
        }}
      >
        <form onSubmit={onSubmit} className="w-full space-y-4 sm:space-y-6">
          <div className="text-center border-b pb-3 sm:pb-4">
            <h1 className="text-base sm:text-xl font-semibold text-blue-700">
              Split Transaction
            </h1>
          </div>

          <div className="flex flex-col sm:flex-row sm:justify-between gap-2 sm:items-center bg-gray-50 p-3 sm:p-4 rounded-md">
            <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2 min-w-0">
              <span className="text-xs text-gray-500 uppercase tracking-wide shrink-0">
                Gateway Reference:
              </span>
              <span className="text-xs sm:text-sm font-medium text-gray-900 truncate">
                {getDisplayValue(gatewayReference)}
              </span>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2">
              <span className="text-xs text-gray-500 uppercase tracking-wide shrink-0">
                Date:
              </span>
              <span className="text-gray-500 text-xs sm:text-sm">
                {formatDateTimeToLocale(transactionDate)}
              </span>
            </div>
          </div>

          <dl className="space-y-2.5 sm:space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-2">
              <dt className="text-xs sm:text-sm text-gray-500 font-semibold sm:w-44 shrink-0">
                Status:
              </dt>
              <dd>
                <CustomBadge color={mappedStatus} size="sm">
                  {backendStatus || "UNKNOWN"}
                </CustomBadge>
              </dd>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-2">
              <dt className="text-xs sm:text-sm text-gray-500 font-semibold sm:w-44 shrink-0">
                Transaction Amount:
              </dt>
              <dd className="text-sm sm:text-base font-bold text-gray-800">
                {formatKES(amount)}
              </dd>
            </div>
          </dl>

          <div className="space-y-4 sm:space-y-6">
            <Controller
              name="channel"
              control={control}
              rules={{
                validate: (v) => splitFormValidators.channel(v) ?? true,
              }}
              render={({ field, fieldState }) => (
                <CustomSelect
                  label="Split to channel:"
                  data={SPLIT_CHANNEL_OPTIONS}
                  comboboxProps={{ withinPortal: false }}
                  required
                  size="sm"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={fieldState.error?.message}
                  disabled={isSubmitting}
                />
              )}
            />
            <Controller
              name="amount"
              control={control}
              rules={{
                validate: (v) =>
                  splitFormValidators.amount(v, amount) ?? true,
              }}
              render={({ field, fieldState }) => (
                <CustomNumberInput
                  label="Amount to Split (Ksh)"
                  withAsterisk
                  min={1}
                  max={amount}
                  clampBehavior="none"
                  radius="md"
                  placeholder="Enter amount"
                  size="sm"
                  disabled={isSubmitting}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={fieldState.error?.message}
                />
              )}
            />
          </div>

          <div>
            <CustomButton
              type="submit"
              fullWidth
              radius="md"
              size="sm"
              disabled={isSubmitting}
              loading={isSubmitting}
            >
              Submit
            </CustomButton>
          </div>
        </form>
      </div>
    </CustomModal>
  );
};

export default SplitTransactionModal;
